import React from 'react';

/**
 * Public-policy link row. Rendered on the sign-in screen and inside the app so
 * the privacy policy, terms and data-deletion route are always one click away —
 * a requirement of Meta's platform review and of GDPR art. 13 transparency.
 *
 * Links are plain anchors, not SPA navigation: /privacy, /terms and
 * /data-deletion are served outside the auth gate, and a signed-out visitor
 * (or a platform reviewer) must be able to open them directly.
 */
export const LegalLinks: React.FC<{
  /** 'dark' for the sign-in screen, 'light' for in-app surfaces. */
  tone?: 'dark' | 'light';
  /** Open in a new tab — use inside the app so work in progress isn't lost. */
  newTab?: boolean;
  align?: 'center' | 'flex-start';
  labels?: { privacy: string; terms: string; deletion: string };
  style?: React.CSSProperties;
  /** New interface on a page without a session (pages/publicUi.ts): colors
   *  from the theme tokens (pages/public.css) instead of `tone`. */
  v2?: boolean;
}> = ({
  tone = 'dark',
  newTab = false,
  align = 'center',
  labels = { privacy: 'Privacy Policy', terms: 'Terms of Service', deletion: 'Data Deletion' },
  style,
  v2 = false,
}) => {
  const base = tone === 'dark' ? '#5A6B85' : '#94A3B8';
  const hover = tone === 'dark' ? '#A78BFA' : '#7C3AED';

  const linkStyle: React.CSSProperties = {
    color: base, fontSize: 11, textDecoration: 'none',
    letterSpacing: '0.02em', transition: 'color 80ms',
  };

  const items: [string, string][] = [
    ['/privacy', labels.privacy],
    ['/terms', labels.terms],
    ['/data-deletion', labels.deletion],
  ];

  return (
    <div style={{
      display: 'flex', gap: 14, alignItems: 'center', justifyContent: align,
      flexWrap: 'wrap', ...style,
    }}>
      {items.map(([href, label], i) => (
        <React.Fragment key={href}>
          {i > 0 && (v2
            ? <span className="pub-legal__sep">·</span>
            : <span style={{ color: base, fontSize: 11, opacity: 0.5 }}>·</span>)}
          <a
            href={href}
            {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            {...(v2 ? { className: 'pub-legal__link' } : { style: linkStyle })}
            onMouseEnter={(e) => { if (!v2) (e.currentTarget as HTMLElement).style.color = hover; }}
            onMouseLeave={(e) => { if (!v2) (e.currentTarget as HTMLElement).style.color = base; }}
          >
            {label}
          </a>
        </React.Fragment>
      ))}
    </div>
  );
};

export default LegalLinks;
